import {
  Component,
  computed,
  effect,
  HostListener,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  HeaderComponent,
  SOFTLINE_FEATURE_TITLE,
  Step,
  StepHeaderComponent,
  TitleStore,
} from '@softline/application';
import { UiCoreModule } from '@softline/ui-core';
import { SOFTLINE_FEATURE_LAGERKORREKTUR } from '../../lagerkorrektur.shared';
import { LagerkorrekturStore } from '../../store';
import { QuantityControl } from '@softapps/lager/core';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'soft-mengen-auswahl',
  imports: [
    CommonModule,
    UiCoreModule,
    StepHeaderComponent,
    HeaderComponent,
    QuantityControl,
  ],
  templateUrl: './mengen-auswahl.page.html',
  styleUrls: ['./mengen-auswahl.page.css'],
})
export class MengenAuswahlPage implements OnInit {
  readonly steps: Step[] = [
    {
      header: '#LAGERKORREKTUR.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERKORREKTUR.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR.PAGES.LAGERSYMBOL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR.PAGES.LAGERSYMBOL_AUSWAHL.DESCRIPTION',
    },
  ];

  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR,
    LagerkorrekturStore.getters.lagerplatz
  );

  artikel$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR,
    LagerkorrekturStore.getters.artikel
  );

  private readonly productQuantity = toSignal(this.artikel$);
  private readonly quantityChanged = signal(false);
  private readonly quantityControl = viewChild(QuantityControl);

  readonly canContinue = computed(() => {
    const artikelMenge = this.productQuantity();
    if (!artikelMenge) return false;
    if (artikelMenge.arteheingabePflicht === true) return this.quantityChanged();
    return true;
  });

  constructor(
    private store: Store,
    private router: Router,
    private backNavigationService: BackNavigationService
  ) {
    // When `arteheingabePflicht` === true we open the modal instantly
    // `quantityChanged` determines if the quantity has been changed or not (e.g. modal dismissed)
    // Based on `arteheingabePflicht` and `quantityChanged` we can determine if the user can continue
    effect(async () => {
      const artikelMenge = this.productQuantity();
      const comp = this.quantityControl();
      if (!artikelMenge || !comp) return;

      if (
        artikelMenge?.arteheingabe &&
        artikelMenge?.arteheingabePflicht === true &&
        !untracked(() => this.quantityChanged())
      ) {
        await untracked(() => comp.openUnitEntryModal());
      }
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERKORREKTUR.TITLE'
    );
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur/artikel-auswahl']);
  }

  async onMengeChange(params: {
    quantity: number | null | undefined;
    baseQuantity?: number | null;
    unitEntryId?: number | null;
  }): Promise<void> {
    this.quantityChanged.set(true);

    const artikel = this.store.get(
      SOFTLINE_FEATURE_LAGERKORREKTUR,
      LagerkorrekturStore.getters.artikel
    );

    this.store.commit(
      SOFTLINE_FEATURE_LAGERKORREKTUR,
      LagerkorrekturStore.mutations.setArtikel,
      {
        ...artikel,
        menge: params.baseQuantity ?? 0,
        menge_lg: params.quantity ?? 0,
        idarteheingabe: params?.unitEntryId ?? undefined,
      }
    );
  }

  @HostListener('window:keydown.enter', ['$event'])
  async onSubmit(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur', 'lagersymbol-auswahl']);
  }
}
